import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import {
  getProfileGroups,
  getGroups,
  addProfileToGroup,
  removeProfileFromGroup,
} from "utils/groupUtils";
import { Group, GroupType } from "models/profile/Group";
import { TwoSizeChip } from "components/alove/Chips";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Autocomplete, TextField,  Alert, Grid, Button } from "@mui/material";
import { addAlert, useMaterialUIController } from "context";
import { Delete } from "@mui/icons-material";

const MDButton = require("components/MDButton").default;

export const ProfileGroups = ({ profileId }: { profileId: string }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [addingToGroup, setAddingToGroup] = useState(false);
  const [removingFromGroup, setRemovingFromGroup] = useState<string | null>(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();

  useEffect(() => {
    if (!profileId) return;

    setLoading(true);
    Promise.all([getProfileGroups(profileId), getGroups()])
      .then(([profileGroups, allGroupsData]) => {
        setGroups(profileGroups.map((group) => Object.assign(new Group(), group)));
        setAllGroups(allGroupsData.map((group) => Object.assign(new Group(), group)));
      })
      .catch((error) => {
        console.error("Error fetching groups:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [profileId]);

  const handleAddToGroup = async () => {
    if (!selectedGroup) {
      setError("Please select a group");
      return;
    }

    setAddingToGroup(true);
    setError("");

    try {
      await addProfileToGroup(selectedGroup.id, profileId);
      addAlert(dispatch, "Profile added to group successfully");
      setSelectedGroup(null);

      // Refresh the profile groups
      const updatedGroups = await getProfileGroups(profileId);
      setGroups(updatedGroups.map((group) => Object.assign(new Group(), group)));
    } catch (error) {
      setError("Failed to add profile to group. Please try again.");
      console.error("Error adding profile to group:", error);
    } finally {
      setAddingToGroup(false);
    }
  };

  const handleRemoveFromGroup = async (groupId: string) => {
    setRemovingFromGroup(groupId);

    try {
      await removeProfileFromGroup(groupId, profileId);
      addAlert(dispatch, "Profile removed from group successfully");

      // Refresh the profile groups
      const updatedGroups = await getProfileGroups(profileId);
      setGroups(updatedGroups.map((group) => Object.assign(new Group(), group)));
    } catch (error) {
      addAlert(dispatch, "Failed to remove profile from group", true);
      console.error("Error removing profile from group:", error);
    } finally {
      setRemovingFromGroup(null);
    }
  };

  const columns: GridColDef[] = [
    {
      headerName: "Name",
      field: "name",
      flex: 1,
    },
    {
      headerName: "Description",
      field: "description",
      flex: 1,
    },
    {
      headerName: "Type",
      field: "type",
      flex: 1,
      renderCell: (params) => {
        const group = params.row as Group;
        return group.type !== undefined && <TwoSizeChip label={GroupType[group.type]} />;
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      renderCell: (params) => {
        const group = params.row as Group;
        return (
          group.statusId !== undefined && (
            <TwoSizeChip label={group.statusLabel} color={group.statusColor as any} />
          )
        );
      },
    },
    {
      headerName: "Created",
      field: "created",
      flex: 1,
      renderCell: (params) => {
        const group = params.row as Group;
        return group.created ? new Date(group.created).toLocaleDateString() : "";
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      renderCell: (params) => {
        const group = params.row as Group;
        return (
            <MDButton
              variant="text"
              color="error"
              startIcon={<Delete />}
              disabled={removingFromGroup === group.id}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFromGroup(group.id);
              }}
            >
              {removingFromGroup === group.id ? "Removing..." : "Remove"}
            </MDButton>
        );
      },
    },
  ];

  // Filter out groups that the profile is already in
  const availableGroups = allGroups.filter(
    (group) => !groups.some((profileGroup) => profileGroup.id === group.id)
  );

  return (
    <Grid container spacing={2}>
      <Grid size={6}>
        <Autocomplete
          sx={{ minWidth: 300 }}
          options={availableGroups}
          getOptionLabel={(option) => option.name}
          value={selectedGroup}
          onChange={(event, newValue) => setSelectedGroup(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Group"
              placeholder="Choose a group to add this profile to"
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Grid>
      <Grid size={6}>
        <MDButton
          variant="contained"
          onClick={handleAddToGroup}
          color="secondary"
          disabled={addingToGroup || !selectedGroup}
        >
          {addingToGroup ? "Adding..." : "Add to Group"}
        </MDButton>
      </Grid>
      <Grid size={12}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading groups...</Typography>
          </Box>
        ) : groups.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="text.secondary">No groups found for this profile</Typography>
          </Box>
        ) : (
          <DataGridPro
            rows={groups}
            columns={columns}
            loading={loading}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(params) => navigate(`/groups/${params.row.id}`)}
            hideFooter
          />
        )}
      </Grid>
    </Grid>
  );
};
